import { DraftError } from 'types/draftError';
import { EntityType } from 'types/entityType';
import { DraftStatus, LiveStatus } from 'utils/statuses';

export interface IdentifiableEntity {
  id: string | null;
  name: string | null;
}

export type Tag = IdentifiableEntity;
export type VenueType = IdentifiableEntity;
export type FormatSize = IdentifiableEntity;

export type BroadsignFrame = {
  broadsignFrameId: string | null;
  displayUnitId: string | null;
};

export type LinkedAssetFrame = {
  spaceId: string;
  name: string | null;
  broadsignFrames: BroadsignFrame[] | null;
};

export type TWeekday =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export type TOperatingHours = Record<TWeekday, Array<string | null> | null>;

export interface IBaseEntity {
  spaceId: string;
  name: string | null;
  mediaOwnerName: string | null;
  environment: string | null;
  formatSize: FormatSize | null;
  internetAccessAvailable: boolean | null;
  averageWeeklyImpactsAvailable: number | null;
  planningRateCpm: string | null;
  currency: string | null;
  audienceDataSource: string | null;
  venueType: VenueType | null;
  auctionToSpotTimeMinutes: number | null;
  allowedMimeTypes: string | null;
  operatingHours: TOperatingHours | null;
  tags: Tag[] | null;
  latitude: number | null;
  longitude: number | null;
  zip: string | null;
  nativeResolution: string | null;
  consolidatedResolutions: string[] | null;
  width: number | null;
  height: number | null;
  entityType: EntityType;
}

export interface IDraftEntity extends IBaseEntity {
  status: DraftStatus;
  draftErrors?: DraftError[];
  intendedLiveStatus: LiveStatus;
}

export interface ILiveEntity extends IBaseEntity {
  status: LiveStatus;
  hasDraft: boolean;
}

export interface IFrame extends IBaseEntity {
  broadsignFrames: BroadsignFrame[] | null;
}

export interface ILinkedAsset extends IBaseEntity {
  linkedAssetFrames: LinkedAssetFrame[] | null;
}

export interface IDraftFrame extends IDraftEntity, IFrame {}

export interface IDraftLinkedAsset extends IDraftEntity, ILinkedAsset {}

export interface ILiveFrame extends ILiveEntity, IFrame {}

export interface ILiveLinkedAsset extends ILiveEntity, ILinkedAsset {}

export type Entity = IFrame | ILinkedAsset;
export type TDraftFrameOrLinkedAsset = IDraftFrame | IDraftLinkedAsset;
export type TLiveFrameOrLinkedAsset = ILiveFrame | ILiveLinkedAsset;

export interface IDraftEntityResponse {
  draft: TDraftFrameOrLinkedAsset;
  previous: TLiveFrameOrLinkedAsset;
}

export const isFrame = (entity: Entity): entity is IFrame => {
  return entity.entityType === EntityType.FRAME;
};

export const isLinkedAsset = (entity: Entity): entity is ILinkedAsset => {
  return entity.entityType === EntityType.LINKED_ASSET;
};
